export type OverlapCluster = {
  id: string;
  lat: number;
  lng: number;
  tripKeys: string[];
};

const METERS_PER_DEGREE_LAT = 111320;

// How far apart two vehicles can be and still count as travelling the same
// road. Opposite carriageways and a frontage road fall inside this; a
// parallel arterial a few blocks over does not.
const SHARE_DISTANCE_METERS = 150;

// Positions are bucketed into a grid. Cells start fine for accuracy and are
// only coarsened when a call is big enough that the fine size would be slow
// - see chooseCellSize.
const BASE_CELL_SIZE_METERS = 60;

// A vehicle reporting every couple of minutes leaves fixes a mile apart, so
// the road *between* two consecutive fixes is walked rather than just
// marking the fixes themselves. Without this a trail is a row of
// unconnected islands and nothing ever joins up.
//
// The step stays under a cell's diagonal so a diagonal run can't skip a
// cell - that guarantees consecutive steps land in the same or an
// 8-adjacent cell, which is what lets plain adjacency carry along-path
// continuity below.
const stepForCell = (cellSizeMeters: number) => cellSizeMeters / 1.5;

// Work scales with total distance driven, not with how many fixes came
// back, so a whole fleet's day (tens of thousands of km) is the case that
// needs bounding. Past this many raster steps the cells grow to compensate:
// detection gets coarser at fleet scale rather than blocking the page. At
// the selection sizes people actually work in this never triggers.
const RASTER_STEP_BUDGET = 400000;

const chooseCellSize = (totalPathMeters: number) => {
  const stepsAtBaseSize = totalPathMeters / stepForCell(BASE_CELL_SIZE_METERS);
  if (stepsAtBaseSize <= RASTER_STEP_BUDGET) return BASE_CELL_SIZE_METERS;
  return BASE_CELL_SIZE_METERS * (stepsAtBaseSize / RASTER_STEP_BUDGET);
};

// A jump this long isn't a road, it's a glitch - a fix that landed in the
// wrong state - so no road is claimed across it.
//
// This has to stay well clear of normal reporting intervals. A truck at
// highway speed covers 3.8km between fixes on a 2-minute interval and
// nearly 19km on a 10-minute one, and skipping those segments leaves the
// trail as a row of unconnected fixes - which shows up as a badge every few
// miles down a shared corridor instead of one badge for the whole thing.
// The map itself draws a straight line between consecutive fixes however
// far apart they are, so this stays generous enough to match what's on
// screen.
const MAX_SEGMENT_METERS = 50000;

// A shared road becomes one badge however long it runs. Where corridors meet
// this can chain a component a long way, so growth stops past this extent.
//
// This is the right default when the trips belong to *different* assets:
// "these vehicles share this corridor" is one fact, and one badge states it.
// It is the wrong answer when one asset retraces its own route - every day
// shares the whole route, so the entire thing collapses to a single badge
// reading "35" that says nothing about where. Callers in that situation pass
// a smaller span via `maxSpanMeters` to get the corridor broken into a run of
// badges instead. See detectOverlapClusters' options.
const MAX_COMPONENT_SPAN_METERS = 1500000;

// How far apart two pieces of the same shared road can sit and still be
// welded back into one badge - see the bridging pass. This has to clear the
// gaps that open when two vehicles' straight lines between fixes drift past
// the sharing tolerance, which at a few kilometres between fixes runs to
// several segments' worth. Merging also requires a trip in common, so a
// generous distance here doesn't pull unrelated overlaps together.
const BRIDGE_DISTANCE_METERS = 25000;

type LatLng = { lat: number; lng: number };

// one run of shared road, before the bridging pass welds interrupted runs
// back together. extents are in metres from the projection origin.
type Stretch = {
  tripKeys: Set<string>;
  busiest: Cell;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  cellCount: number;
};

type Cell = {
  ix: number;
  iy: number;
  // metres from the projection origin, averaged over the points in the cell
  x: number;
  y: number;
  trips: Set<string>;
  sumLat: number;
  sumLng: number;
  count: number;
};

// Numeric keys rather than strings: these are built and looked up millions
// of times across a fleet and string keys dominated the cost. The offset
// keeps negative coordinates positive and the grid it allows is far larger
// than the planet.
const GRID_INDEX_OFFSET = 2097152;
const GRID_INDEX_STRIDE = 4194304;
const cellKeyOf = (ix: number, iy: number) =>
  (ix + GRID_INDEX_OFFSET) * GRID_INDEX_STRIDE + (iy + GRID_INDEX_OFFSET);

const BUCKET_INDEX_OFFSET = 65536;
const BUCKET_INDEX_STRIDE = 131072;
const bucketKeyOf = (bx: number, by: number) =>
  (bx + BUCKET_INDEX_OFFSET) * BUCKET_INDEX_STRIDE + (by + BUCKET_INDEX_OFFSET);

const ADJACENT_OFFSETS: [number, number][] = [
  [-1, -1],
  [-1, 0],
  [-1, 1],
  [0, -1],
  [0, 1],
  [1, -1],
  [1, 0],
  [1, 1],
];

/**
 * Finds where trips physically coincide - in metres, so the answer doesn't
 * change with zoom - and returns one badge per shared stretch of road,
 * counting every trip that uses it.
 *
 * Each trip's road is walked into a shared grid rather than comparing trips
 * pairwise. A cell counts as shared when more than one trip is on or beside
 * it, and touching shared cells are grown into one component. That is what
 * collapses a long shared corridor into a single badge instead of one per
 * sampled point, and what merges two adjacent overlapping groups into a
 * single badge carrying the union of their trips.
 *
 * Two things worth knowing about the shape of this:
 *
 *  - Sharing is decided by real distance, not by whether two points landed
 *    in the same grid cell. Cell adjacency is a yes/no with a hard edge, and
 *    for vehicles converging on a road at a shallow angle the separation
 *    sits right at that edge for miles, flickering in and out and
 *    shattering one stretch into dozens of badges.
 *  - Connectivity, by contrast, can use plain adjacency, because the raster
 *    step is deliberately smaller than a cell diagonal - consecutive steps
 *    along one trip always land in the same or an adjacent cell.
 */
export const detectOverlapClusters = (
  trips: { tripKey: string; path: LatLng[] }[],
  options?: {
    // How far one badge's stretch may run before the next one takes over.
    // Defaults to "however long the shared road is", which is what you want
    // when the trips are different assets. Pass something on the order of a
    // stretch of highway when they're one asset's repeated runs, so the
    // shared corridor reads as a run of badges - each counting the trips on
    // that piece - rather than a single number sitting on one point.
    maxSpanMeters?: number;
  }
): OverlapCluster[] => {
  const usable = trips.filter((trip) => trip.path.length > 0);
  if (usable.length < 2) return [];

  const maxSpanMeters = options?.maxSpanMeters ?? MAX_COMPONENT_SPAN_METERS;

  const origin = usable[0].path[0];
  const lngScale = Math.cos((origin.lat * Math.PI) / 180) || 1;
  const xOf = (lng: number) =>
    (lng - origin.lng) * METERS_PER_DEGREE_LAT * lngScale;
  const yOf = (lat: number) => (lat - origin.lat) * METERS_PER_DEGREE_LAT;

  // one cheap pass to size the grid before the expensive one fills it
  let totalPathMeters = 0;
  usable.forEach((trip) => {
    for (let i = 1; i < trip.path.length; i++) {
      const dx = xOf(trip.path[i].lng) - xOf(trip.path[i - 1].lng);
      const dy = yOf(trip.path[i].lat) - yOf(trip.path[i - 1].lat);
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance <= MAX_SEGMENT_METERS) totalPathMeters += distance;
    }
  });

  const cellSizeMeters = chooseCellSize(totalPathMeters);
  const rasterStep = stepForCell(cellSizeMeters);

  // buckets index the cells for proximity lookups, so they have to be at
  // least as coarse as both the cells and the distance being searched
  const bucketSizeMeters = Math.max(SHARE_DISTANCE_METERS, cellSizeMeters);

  const cells = new Map<number, Cell>();
  const buckets = new Map<number, number[]>();

  const markCell = (
    tripKey: string,
    ix: number,
    iy: number,
    key: number,
    lat: number,
    lng: number,
    x: number,
    y: number
  ) => {
    const existing = cells.get(key);
    if (existing) {
      existing.trips.add(tripKey);
      existing.sumLat += lat;
      existing.sumLng += lng;
      existing.count += 1;
      return;
    }
    cells.set(key, {
      ix,
      iy,
      x: 0,
      y: 0,
      trips: new Set([tripKey]),
      sumLat: lat,
      sumLng: lng,
      count: 1,
    });
    const bucketKey = bucketKeyOf(
      Math.floor(x / bucketSizeMeters),
      Math.floor(y / bucketSizeMeters)
    );
    const bucket = buckets.get(bucketKey);
    if (bucket) bucket.push(key);
    else buckets.set(bucketKey, [key]);
  };

  usable.forEach((trip) => {
    // repeated samples inside one cell are skipped - a dense trace puts many
    // consecutive fixes in the same cell and each repeat is a wasted lookup
    let previousKey = NaN;
    const visit = (lat: number, lng: number, x: number, y: number) => {
      const ix = Math.floor(x / cellSizeMeters);
      const iy = Math.floor(y / cellSizeMeters);
      const key = cellKeyOf(ix, iy);
      if (key === previousKey) return;
      markCell(trip.tripKey, ix, iy, key, lat, lng, x, y);
      previousKey = key;
    };

    trip.path.forEach((point, index) => {
      const x = xOf(point.lng);
      const y = yOf(point.lat);
      visit(point.lat, point.lng, x, y);

      const next = trip.path[index + 1];
      if (!next) return;
      const nextX = xOf(next.lng);
      const nextY = yOf(next.lat);
      const distance = Math.sqrt(
        (nextX - x) * (nextX - x) + (nextY - y) * (nextY - y)
      );
      if (distance > MAX_SEGMENT_METERS || distance <= rasterStep) return;

      const steps = Math.ceil(distance / rasterStep);
      for (let step = 1; step < steps; step++) {
        const fraction = step / steps;
        visit(
          point.lat + (next.lat - point.lat) * fraction,
          point.lng + (next.lng - point.lng) * fraction,
          x + (nextX - x) * fraction,
          y + (nextY - y) * fraction
        );
      }
    });
  });

  // each cell's real position, computed once - every distance check below
  // reuses it rather than re-deriving it
  cells.forEach((cell) => {
    cell.x = xOf(cell.sumLng / cell.count);
    cell.y = yOf(cell.sumLat / cell.count);
  });

  const shareDistanceSq = SHARE_DISTANCE_METERS * SHARE_DISTANCE_METERS;

  // Everything within reach of a cell: neighbours by real distance, plus the
  // 8 cells around it. The distance search is what makes sharing robust for
  // trips running close but not co-located; the adjacency is what carries a
  // single trip's own trail forward when cells are coarser than the
  // distance being searched.
  const neighboursOf = (cell: Cell, key: number): number[] => {
    const found: number[] = [];
    const seen = new Set<number>();

    const bx = Math.floor(cell.x / bucketSizeMeters);
    const by = Math.floor(cell.y / bucketSizeMeters);
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const bucket = buckets.get(bucketKeyOf(bx + dx, by + dy));
        if (!bucket) continue;
        for (const candidateKey of bucket) {
          if (candidateKey === key || seen.has(candidateKey)) continue;
          const candidate = cells.get(candidateKey);
          if (!candidate) continue;
          const ddx = cell.x - candidate.x;
          const ddy = cell.y - candidate.y;
          if (ddx * ddx + ddy * ddy > shareDistanceSq) continue;
          seen.add(candidateKey);
          found.push(candidateKey);
        }
      }
    }

    for (const [dx, dy] of ADJACENT_OFFSETS) {
      const adjacentKey = cellKeyOf(cell.ix + dx, cell.iy + dy);
      if (seen.has(adjacentKey) || !cells.has(adjacentKey)) continue;
      seen.add(adjacentKey);
      found.push(adjacentKey);
    }

    return found;
  };

  // computed once per cell and reused for the component walk, so the
  // proximity search never runs twice for the same cell
  const neighbourKeys = new Map<number, number[]>();
  const sharedKeys = new Set<number>();

  cells.forEach((cell, key) => {
    const neighbours = neighboursOf(cell, key);
    neighbourKeys.set(key, neighbours);

    if (cell.trips.size >= 2) {
      sharedKeys.add(key);
      return;
    }
    // one trip of its own - shared only if a *different* trip is close by.
    // adjacency alone isn't enough here: an adjacent cell can be up to a
    // cell-diagonal away, which at coarse sizes is well past the tolerance.
    const own = cell.trips.values().next().value;
    for (const neighbourKey of neighbours) {
      const neighbour = cells.get(neighbourKey);
      if (!neighbour) continue;
      const dx = cell.x - neighbour.x;
      const dy = cell.y - neighbour.y;
      if (dx * dx + dy * dy > shareDistanceSq) continue;
      let hasOtherTrip = false;
      for (const tripKey of neighbour.trips) {
        if (tripKey !== own) {
          hasOtherTrip = true;
          break;
        }
      }
      if (hasOtherTrip) {
        sharedKeys.add(key);
        return;
      }
    }
  });

  const maxCellSpan = maxSpanMeters / cellSizeMeters;
  const visited = new Set<number>();
  const clusters: OverlapCluster[] = [];
  const stretches: Stretch[] = [];

  sharedKeys.forEach((startKey) => {
    if (visited.has(startKey)) return;
    const startCell = cells.get(startKey);
    if (!startCell) return;

    // grow outwards through touching shared cells. consecutive points along
    // a road land in neighbouring cells, so a whole corridor comes back as
    // one component rather than a badge every few miles.
    const queue: number[] = [startKey];
    visited.add(startKey);
    const component: Cell[] = [startCell];
    let minIx = startCell.ix;
    let maxIx = startCell.ix;
    let minIy = startCell.iy;
    let maxIy = startCell.iy;

    while (queue.length > 0) {
      const key = queue.shift()!;
      const neighbours = neighbourKeys.get(key) ?? [];
      for (const neighbourKey of neighbours) {
        if (visited.has(neighbourKey) || !sharedKeys.has(neighbourKey)) continue;
        const neighbour = cells.get(neighbourKey);
        if (!neighbour) continue;
        // don't let a junction chain one component across the whole map
        if (
          Math.max(maxIx, neighbour.ix) - Math.min(minIx, neighbour.ix) >
            maxCellSpan ||
          Math.max(maxIy, neighbour.iy) - Math.min(minIy, neighbour.iy) >
            maxCellSpan
        )
          continue;

        visited.add(neighbourKey);
        minIx = Math.min(minIx, neighbour.ix);
        maxIx = Math.max(maxIx, neighbour.ix);
        minIy = Math.min(minIy, neighbour.iy);
        maxIy = Math.max(maxIy, neighbour.iy);
        component.push(neighbour);
        queue.push(neighbourKey);
      }
    }

    const tripKeys = new Set<string>();
    // the badge sits where the road is busiest rather than at the middle of
    // the stretch - on a long corridor that puts it on the junction people
    // recognise instead of somewhere out in open country
    let busiest = component[0];
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    component.forEach((cell) => {
      cell.trips.forEach((tripKey) => tripKeys.add(tripKey));
      if (
        cell.trips.size > busiest.trips.size ||
        (cell.trips.size === busiest.trips.size && cell.count > busiest.count)
      )
        busiest = cell;
      if (cell.x < minX) minX = cell.x;
      if (cell.x > maxX) maxX = cell.x;
      if (cell.y < minY) minY = cell.y;
      if (cell.y > maxY) maxY = cell.y;
    });

    if (tripKeys.size < 2) return;

    stretches.push({
      tripKeys,
      busiest,
      minX,
      maxX,
      minY,
      maxY,
      cellCount: component.length,
    });
  });

  // --- bridge the pieces of one shared road back together
  //
  // Two trucks on the same highway don't trace the same line. Their fixes
  // land at different moments, so between fixes each one's straight line
  // cuts the corners differently, and every so often the two drift more
  // than the tolerance apart. That breaks an otherwise continuous shared
  // stretch into a run of pieces - which is what put a badge every few
  // miles down a corridor instead of one badge for the whole thing.
  //
  // Pieces are welded back together when they carry a trip in common and
  // lie close enough to be the same road. Requiring a shared trip is what
  // keeps this honest: two unrelated pairs overlapping near each other stay
  // separate, while one pair's interrupted stretch becomes a single badge.
  // It also gives the union count for free - adjacent groups of three
  // sharing two trips merge into a single badge of four.
  const boxGapMeters = (a: Stretch, b: Stretch) => {
    const dx = Math.max(0, Math.max(a.minX - b.maxX, b.minX - a.maxX));
    const dy = Math.max(0, Math.max(a.minY - b.maxY, b.minY - a.maxY));
    return Math.sqrt(dx * dx + dy * dy);
  };

  const shareATrip = (a: Stretch, b: Stretch) => {
    const [small, large] =
      a.tripKeys.size <= b.tripKeys.size ? [a, b] : [b, a];
    for (const tripKey of small.tripKeys) {
      if (large.tripKeys.has(tripKey)) return true;
    }
    return false;
  };

  // A caller asking for a short span wants the corridor broken up, so the
  // bridge can't reach further than the span it's bridging within - left at
  // the full distance it would simply weld every segment back into the one
  // badge the span cap just split.
  const bridgeDistanceMeters = Math.min(
    BRIDGE_DISTANCE_METERS,
    maxSpanMeters / 2
  );

  let mergedSomething = true;
  while (mergedSomething) {
    mergedSomething = false;
    for (let i = 0; i < stretches.length && !mergedSomething; i++) {
      for (let j = i + 1; j < stretches.length; j++) {
        const a = stretches[i];
        const b = stretches[j];
        if (boxGapMeters(a, b) > bridgeDistanceMeters) continue;
        if (!shareATrip(a, b)) continue;
        // the merged stretch is still one badge, so it answers to the same
        // span cap the component walk did
        if (
          Math.max(a.maxX, b.maxX) - Math.min(a.minX, b.minX) > maxSpanMeters ||
          Math.max(a.maxY, b.maxY) - Math.min(a.minY, b.minY) > maxSpanMeters
        )
          continue;

        b.tripKeys.forEach((tripKey) => a.tripKeys.add(tripKey));
        if (
          b.busiest.trips.size > a.busiest.trips.size ||
          (b.busiest.trips.size === a.busiest.trips.size &&
            b.busiest.count > a.busiest.count)
        )
          a.busiest = b.busiest;
        a.minX = Math.min(a.minX, b.minX);
        a.maxX = Math.max(a.maxX, b.maxX);
        a.minY = Math.min(a.minY, b.minY);
        a.maxY = Math.max(a.maxY, b.maxY);
        a.cellCount += b.cellCount;

        stretches.splice(j, 1);
        mergedSomething = true;
        break;
      }
    }
  }

  stretches.forEach((stretch) => {
    clusters.push({
      id: `overlap-cluster-${clusters.length}`,
      lat: stretch.busiest.sumLat / stretch.busiest.count,
      lng: stretch.busiest.sumLng / stretch.busiest.count,
      tripKeys: Array.from(stretch.tripKeys),
    });
  });


  return clusters;
};
